import { X } from "lucide-react";
import { useState, type JSX, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/shared/ui/button";
import { Drawer } from "@/shared/ui/drawer";
import { Select } from "@/shared/ui/select";

import { useCouriers } from "../api/use-couriers";
import type { OrderFilters } from "../api/use-orders";
import { usePaymentChannels } from "../api/use-payment-channels";
import {
  countDrawerFilters,
  DRAWER_KEYS,
  LIFECYCLES,
  type FilterUpdate,
} from "../lib/order-url-state";

interface FilterDrawerProps {
  open: boolean;
  onClose: () => void;
  filter: OrderFilters;
  sellers: { marketplace: string; shop_id: string; shop_name?: string }[];
  onApply: (updates: FilterUpdate) => void;
}

type Draft = Pick<OrderFilters, (typeof DRAWER_KEYS)[number]>;

const pick = (f: OrderFilters): Draft =>
  Object.fromEntries(DRAWER_KEYS.map((k) => [k, f[k]])) as Draft;

const LABEL_COL = "w-[140px] shrink-0 text-sm font-medium text-[var(--text-secondary)]";
const ROW = "flex items-center gap-4";
const EYEBROW = "mb-3 text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)]";

interface SelectRowProps {
  label: string;
  testId: string;
  value: string;
  onChange: (v: string) => void;
  options: [string, string][];
  hint?: string | undefined;
}

function SelectRow({ label, testId, value, onChange, options, hint }: SelectRowProps): JSX.Element {
  return (
    <div className={ROW}>
      <span className={LABEL_COL}>{label}</span>
      <div className="flex min-w-0 flex-col gap-1">
        <Select
          value={value}
          data-testid={testId}
          aria-label={label}
          onChange={(e) => onChange(e.target.value)}
        >
          {options.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </Select>
        {hint && <span className="text-xs text-[var(--text-subtle)]">{hint}</span>}
      </div>
    </div>
  );
}

interface RadioRowProps {
  label: string;
  name: string;
  value: string | undefined;
  onChange: (v: string) => void;
  options: [string, string][];
}

function RadioRow({ label, name, value, onChange, options }: RadioRowProps): JSX.Element {
  return (
    <div className={ROW}>
      <span className={LABEL_COL}>{label}</span>
      <div className="flex flex-wrap items-center gap-4">
        {options.map(([v, l]) => (
          <label key={v} className="flex items-center gap-1.5 text-sm">
            <input
              type="radio"
              name={name}
              aria-label={l}
              checked={(value ?? "") === v}
              onChange={() => onChange(v)}
              className="h-4 w-4"
              style={{ accentColor: "var(--brand-500)" }}
            />
            {l}
          </label>
        ))}
      </div>
    </div>
  );
}

/**
 * Owns the draft state and is keyed by `open` from the wrapper below — switching
 * false->true remounts it, which reseeds `draft` straight from `filter` via the
 * `useState` initializer. That's simpler than an effect (no cascading setState,
 * no stale-draft flash) and the Drawer chrome itself (and its slide transition)
 * lives one level up, unaffected by this remount.
 */
function FilterDrawerBody({
  onClose,
  filter,
  sellers,
  onApply,
}: Omit<FilterDrawerProps, "open">): JSX.Element {
  const { t } = useTranslation("orders");
  const [draft, setDraft] = useState<Draft>(() => pick(filter));
  const { data: couriers = [] } = useCouriers();
  const { data: channels = [] } = usePaymentChannels();
  const set = <K extends keyof Draft>(k: K, v: Draft[K] | undefined): void =>
    setDraft((d) => ({ ...d, [k]: v }));
  const count = countDrawerFilters(draft);
  const paymentChannels = (draft.payment_channel ?? "").split(",").filter(Boolean);

  function apply(): void {
    const updates: FilterUpdate = { offset: 0 };
    for (const k of DRAWER_KEYS) {
      const v = draft[k];
      (updates as Record<string, unknown>)[k] = Array.isArray(v)
        ? v.length
          ? v
          : undefined
        : v || undefined;
    }
    onApply(updates);
    onClose();
  }

  const footer: ReactNode = (
    <div className="sticky bottom-0 flex justify-between border-t border-[var(--premium-border)] bg-white px-5 py-3">
      <Button variant="ghost" size="sm" onClick={() => setDraft({} as Draft)}>
        {t("btn_reset")}
      </Button>
      <Button variant="default" size="sm" onClick={apply} data-testid="drawer-apply">
        {count > 0 ? t("btn_apply_n", { n: count }) : t("btn_apply")}
      </Button>
    </div>
  );

  return (
    <>
      <div className="flex flex-col gap-6 p-5">
        <section>
          <h3 className={EYEBROW}>{t("drawer_group_fulfilment")}</h3>
          <div className="flex flex-col gap-4">
            <div className={`${ROW} items-start`}>
              <span className={`${LABEL_COL} pt-2`}>{t("drawer_stage")}</span>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                {LIFECYCLES.map((code) => (
                  <label key={code} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={draft.status?.includes(code) ?? false}
                      onChange={(e) =>
                        set(
                          "status",
                          e.target.checked
                            ? [...(draft.status ?? []), code]
                            : (draft.status ?? []).filter((s) => s !== code),
                        )
                      }
                      className="h-4 w-4 rounded"
                      style={{ accentColor: "var(--brand-500)" }}
                    />
                    {/* ponytail: st_picked is "Confirm pick" (an action label reused by the
                        quick chip bar / StuckTable) — the drawer checkbox needs the bare
                        stage name instead, so "picked" alone gets its own key. */}
                    {t(code === "picked" ? "drawer_stage_picked" : `st_${code}`)}
                  </label>
                ))}
              </div>
            </div>
            <RadioRow
              label={t("drawer_label")}
              name="awb"
              value={draft.awb}
              onChange={(v) => set("awb", v as Draft["awb"])}
              options={[
                ["", t("any")],
                ["missing", t("awb_missing_title")],
                ["present", t("awb_present_title")],
              ]}
            />
            <SelectRow
              label={t("drawer_sof")}
              testId="drawer-sof"
              value={draft.sof ?? ""}
              onChange={(v) => set("sof", (v || undefined) as Draft["sof"])}
              options={[
                ["", t("any")],
                ["false", t("sof_excluded_title")],
                ["true", t("sof_only_title")],
              ]}
            />
            <SelectRow
              label={t("drawer_courier")}
              testId="drawer-courier"
              value={draft.courier ?? ""}
              onChange={(v) => set("courier", v || undefined)}
              options={[
                ["", t("any")],
                ...couriers.map((c) => [c.value, c.value] as [string, string]),
              ]}
            />
            <RadioRow
              label={t("drawer_ship_by")}
              name="ship_by"
              value={draft.ship_by}
              onChange={(v) => set("ship_by", v as Draft["ship_by"])}
              options={[
                ["", t("any")],
                ["overdue", t("ship_by_overdue_title")],
                ["today", t("ship_by_today_title")],
                ["next_24h", t("ship_by_next_24h_title")],
              ]}
            />
          </div>
        </section>
        <section>
          <h3 className={EYEBROW}>{t("drawer_group_order")}</h3>
          <div className="flex flex-col gap-4">
            <SelectRow
              label={t("drawer_gift")}
              testId="drawer-gift"
              value={draft.gift ?? ""}
              onChange={(v) => set("gift", (v || undefined) as Draft["gift"])}
              options={[
                ["", t("any")],
                ["true", t("chip_gift_only")],
                ["false", t("chip_gift_none")],
              ]}
            />
            <div className={`${ROW} items-start`}>
              <span className={`${LABEL_COL} pt-2`}>{t("drawer_payment_channel")}</span>
              <div className="flex flex-col gap-2">
                <Select
                  value=""
                  data-testid="drawer-payment-channel"
                  aria-label={t("drawer_payment_channel")}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v && !paymentChannels.includes(v))
                      set("payment_channel", [...paymentChannels, v].join(","));
                  }}
                >
                  <option value="">{t("any")}</option>
                  {channels.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.value}
                    </option>
                  ))}
                </Select>
                {paymentChannels.length > 0 && (
                  // ponytail: shared FilterChips always renders its own "Clear all" button
                  // too, which this per-field chip row doesn't need (the drawer's own
                  // Reset already clears everything) — a few inline buttons is less than
                  // wrapping/repurposing that component for a case it doesn't fit.
                  <div
                    data-testid="drawer-payment-chips"
                    className="flex flex-wrap items-center gap-2"
                  >
                    {paymentChannels.map((ch) => (
                      <button
                        key={ch}
                        type="button"
                        onClick={() =>
                          set(
                            "payment_channel",
                            paymentChannels.filter((x) => x !== ch).join(",") || undefined,
                          )
                        }
                        className="inline-flex items-center gap-1 rounded-full border border-[var(--border-default)] bg-[var(--surface-sunken)] px-2 py-0.5 text-xs text-[var(--text-primary)] hover:bg-[var(--ink-100)] focus-ring transition-colors"
                      >
                        <span>{ch}</span>
                        <X className="h-3 w-3" aria-hidden />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <SelectRow
              label={t("drawer_seller")}
              testId="drawer-seller"
              value={draft.seller_id ?? ""}
              onChange={(v) => set("seller_id", v || undefined)}
              options={[
                ["", t("any")],
                ...sellers.map((s) => [s.shop_id, s.shop_name || s.shop_id] as [string, string]),
              ]}
            />
          </div>
        </section>
      </div>
      {footer}
    </>
  );
}

export function FilterDrawer(props: FilterDrawerProps): JSX.Element {
  const { t } = useTranslation("orders");
  return (
    <Drawer
      open={props.open}
      onClose={props.onClose}
      width={440}
      title={t("drawer_title")}
      subtitle={t("drawer_subtitle")}
      data-testid="filter-drawer"
    >
      <FilterDrawerBody key={String(props.open)} {...props} />
    </Drawer>
  );
}
